import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const LOCATION_STORAGE_KEY = "user_location";

const getStoredLocation = () => {
  try {
    const storedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
    return storedLocation ? JSON.parse(storedLocation) : null;
  } catch {
    localStorage.removeItem(LOCATION_STORAGE_KEY);
    return null;
  }
};

export const requestCurrentLocation = createAsyncThunk(
  "location/requestCurrentLocation",
  async (_, { rejectWithValue }) => {
    if (!navigator.geolocation) {
      return rejectWithValue({
        status: "unsupported",
        message: "Location is not supported by this browser.",
      });
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            updated_at: new Date().toISOString(),
          });
        },
        (error) => {
          reject(
            rejectWithValue({
              status: error.code === error.PERMISSION_DENIED ? "denied" : "failed",
              message: error.message || "Unable to fetch location. Please try again.",
            })
          );
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    });
  }
);

export const checkLocationPermission = createAsyncThunk(
  "location/checkLocationPermission",
  async (_, { dispatch }) => {
    if (!navigator.permissions) {
      return "unknown";
    }

    try {
      const result = await navigator.permissions.query({
        name: "geolocation",
      });

      dispatch(setPermissionStatus(result.state));

      // Auto-fetch location if already allowed
      if (result.state === "granted") {
        dispatch(requestCurrentLocation());
      }

      return result.state;
    } catch {
      return "unknown";
    }
  }
);

const initialState = {
  location: getStoredLocation(),
  permissionStatus: "unknown",
  status: "idle",
  message: "",
  promptDismissed: false,
};

const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    setPermissionStatus: (state, action) => {
      state.permissionStatus = action.payload || "unknown";
      if (action.payload === "granted" && state.location) {
        state.status = "succeeded";
        state.message = "Location access is turned on.";
      }
    },
    dismissLocationPrompt: (state) => {
      state.promptDismissed = true;
    },
    openLocationPrompt: (state) => {
      state.promptDismissed = false;
    },
    clearLocation: (state) => {
      state.location = null;
      state.status = "idle";
      state.message = "";
      localStorage.removeItem(LOCATION_STORAGE_KEY);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestCurrentLocation.pending, (state) => {
        state.status = "requesting";
        state.message = "Waiting for location permission...";
      })
      .addCase(requestCurrentLocation.fulfilled, (state, action) => {
        state.location = action.payload;
        state.status = "succeeded";
        state.permissionStatus = "granted";
        state.message = "Location access is turned on.";
        state.promptDismissed = true;
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(action.payload));
      })
      .addCase(requestCurrentLocation.rejected, (state, action) => {
        state.location = null;
        state.status = action.payload?.status || "failed";
        state.permissionStatus =
          action.payload?.status === "denied" ? "denied" : state.permissionStatus;
        state.message =
          action.payload?.message || "Unable to fetch location. Please try again.";
      })
      .addCase(checkLocationPermission.fulfilled, (state, action) => {
        state.permissionStatus = action.payload;
      });
  },
});

export const {
  clearLocation,
  dismissLocationPrompt,
  openLocationPrompt,
  setPermissionStatus,
} = locationSlice.actions;

export default locationSlice.reducer;
